import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { notificationService } from '../../services/notificationService';
import { academicService } from '../../services/academicService';
import { userService } from '../../services/userService';
import { assignmentService } from '../../services/assignmentService';
import { communicationService } from '../../services/communicationService';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  FileText,
  Clock,
  AlertCircle
} from 'lucide-react';

const StaffDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = React.useState({
    totalStudents: 0,
    activeClasses: 0,
    pendingTasks: 0,
    meetingsToday: 0
  });
  const [recentTasks, setRecentTasks] = React.useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = React.useState([]);
  const [classSchedule, setClassSchedule] = React.useState([]);
  const [studentAlerts, setStudentAlerts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const run = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch statistics
        const [studentsRes, classesRes, assignmentsRes, announcementsRes, timetableRes, notificationsRes] = await Promise.all([
          userService.getStudents({ limit: 1 }).catch(() => ({ data: { data: { students: [], pagination: { total: 0 } } } })),
          academicService.getClasses({ teacherId: user.id, limit: 1 }).catch(() => ({ data: { data: { classes: [], pagination: { total: 0 } } } })),
          assignmentService.getAssignments({ teacherId: user.id, status: 'published', limit: 1 }).catch(() => ({ data: { data: { assignments: [], pagination: { total: 0 } } } })),
          communicationService.getAnnouncements({ isPublished: true, limit: 10 }).catch(() => ({ data: { data: { announcements: [] } } })),
          academicService.getMyTimetable({ today: true }).catch(() => ({ data: { data: { timetables: [] } } })),
          notificationService.getNotifications({ limit: 10, priority: 'high' }).catch(() => ({ data: { data: [] } }))
        ]);

        // Set statistics
        setStats({
          totalStudents: studentsRes.data?.data?.pagination?.total || studentsRes.data?.data?.students?.length || 0,
          activeClasses: classesRes.data?.data?.pagination?.total || classesRes.data?.data?.classes?.length || 0,
          pendingTasks: assignmentsRes.data?.data?.pagination?.total || assignmentsRes.data?.data?.assignments?.length || 0,
          meetingsToday: announcementsRes.data?.data?.announcements?.filter(a => {
            const scheduledDate = a.scheduledFor ? new Date(a.scheduledFor) : new Date(a.createdAt);
            const today = new Date();
            return scheduledDate.toDateString() === today.toDateString();
          }).length || 0
        });

        // Fetch pending assignments for recent tasks (published assignments that need grading)
        const tasksRes = await assignmentService.getAssignments({ teacherId: user.id, status: 'published', limit: 3 }).catch(() => ({ data: { data: { assignments: [] } } }));
        setRecentTasks((tasksRes.data?.data?.assignments || []).map((a, i) => ({
          id: a.id || i,
          title: a.title || 'Assignment',
          priority: a.priority || 'medium',
          dueDate: a.dueDate ? new Date(a.dueDate).toLocaleDateString() : 'No due date',
          status: 'pending'
        })));

        // Fetch announcements for upcoming meetings
        const meetings = (announcementsRes.data?.data?.announcements || [])
          .filter(a => a.type === 'meeting' || a.category === 'meeting')
          .slice(0, 3)
          .map((a, i) => ({
            id: a.id || i,
            title: a.title || a.message || 'Meeting',
            date: a.scheduledFor ? new Date(a.scheduledFor).toLocaleDateString() : new Date(a.createdAt).toLocaleDateString(),
            time: a.scheduledFor ? new Date(a.scheduledFor).toLocaleTimeString() : new Date(a.createdAt).toLocaleTimeString(),
            type: 'meeting'
          }));
        setUpcomingMeetings(meetings);

        // Fetch today's timetable
        const timetables = timetableRes.data?.data?.timetables || [];
        setClassSchedule(timetables.slice(0, 3).map((t, i) => ({
          id: t.id || i,
          subject: t.subject?.name || 'Subject',
          class: t.class?.name || 'Class',
          time: t.startTime ? `${t.startTime} - ${t.endTime}` : 'TBA',
          room: t.room || 'TBA'
        })));

        // Fetch student alerts from notifications (high priority notifications)
        const alerts = (notificationsRes.data?.data?.notifications || notificationsRes.data?.data || [])
          .filter(n => n.priority === 'high' || n.priority === 'urgent')
          .slice(0, 3)
          .map((n, i) => ({
            id: n.id || i,
            student: n.meta?.studentName || n.meta?.student || 'Student',
            issue: n.message || n.title || 'Notice',
            severity: n.priority === 'high' ? 'high' : n.priority === 'medium' ? 'medium' : 'low'
          }));
        setStudentAlerts(alerts);
      } catch (e) {
        console.error('Error loading dashboard data:', e);
        setRecentTasks([]);
        setUpcomingMeetings([]);
        setClassSchedule([]);
        setStudentAlerts([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user?.id]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Staff Dashboard
          </h1>
          <p className="text-gray-600">
            Welcome, {user?.firstName || 'Staff'}! Manage your administrative tasks and school operations.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <BookOpen className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Classes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeClasses}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingTasks}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Meetings Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.meetingsToday}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Tasks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Recent Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{task.title}</h4>
                      <p className="text-sm text-gray-600">Due: {task.dueDate}</p>
                    </div>
                    <div className="text-right">
                      <Badge 
                        className={
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }
                      >
                        {task.priority}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">{task.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Meetings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Upcoming Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upcomingMeetings.map((meeting) => (
                  <div key={meeting.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{meeting.title}</h4>
                      <p className="text-sm text-gray-600">{meeting.date} at {meeting.time}</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800 capitalize">{meeting.type}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Today's Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Today's Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {classSchedule.map((schedule) => (
                  <div key={schedule.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{schedule.subject}</h4>
                      <p className="text-sm text-gray-600">{schedule.class}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{schedule.time}</p>
                      <p className="text-xs text-gray-500">{schedule.room}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Student Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Student Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {studentAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{alert.student}</h4>
                      <p className="text-sm text-gray-600">{alert.issue}</p>
                    </div>
                    <Badge 
                      className={
                        alert.severity === 'high' ? 'bg-red-100 text-red-800' :
                        alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }
                    >
                      {alert.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <Users className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-sm font-medium">Manage Students</p>
              </button>
              <button className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <BookOpen className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <p className="text-sm font-medium">View Classes</p>
              </button>
              <button className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <FileText className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <p className="text-sm font-medium">Grade Assignments</p>
              </button>
              <button className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-yellow-600" />
                <p className="text-sm font-medium">Schedule Meeting</p>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
  );
};

export default StaffDashboard;
